import React, { useState, useEffect, useMemo, useLayoutEffect, useCallback } from "react";
import { DataGridPro, DataGridProProps, GridCallbackDetails, GridRowModel, useGridApiRef } from "@mui/x-data-grid-pro";
import { alpha, Skeleton, SxProps, Theme } from "@mui/material";
import { useDataGridLocale } from "helpers/hooks/useDataGridLocale";
import { IStatusProperties } from "./DataGridColumnGenerator";
import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import useRowsPerPage from "helpers/hooks/useRowsPerPage";
import useIsMobile from "helpers/hooks/useIsMobile";
import CustomDataGridToolbar from "./CustomDataGridToolbar";
import { isNullOrWhiteSpace } from "helpers/objects";

//FUTURE: Add custom props to this for any custom functionality
interface IProps extends Omit<DataGridProProps, "rows"> {
    rows: (GridRowModel & IStatusProperties)[] | GridRowModel[] | null | undefined;
    tableName: string;
    tableSubName?: string;
    hideByDefaultColumns?: string[];
    hideInMobileColumns?: string[];
    disableToolbar?: boolean;
    disableRestoreGridStyle?: boolean;
    disableGridExport?: boolean;
    columnManagerRestore?: (initialState: GridInitialStatePro, storedState: GridInitialStatePro) => GridInitialStatePro;
}

const CustomDataGridPro: React.FC<IProps> = ({ rows, tableName, tableSubName, hideByDefaultColumns, hideInMobileColumns, 
    disableToolbar, disableRestoreGridStyle, disableGridExport, columnManagerRestore, 
    apiRef, pageSize, rowsPerPageOptions, onPageSizeChange, columns, ...rest }) => {

    const localeText = useDataGridLocale();
    const internalApiRef = useGridApiRef();
    const [showGrid, setShowGrid] = useState<boolean>(false);
    const rowsPerPage = useRowsPerPage(tableName);
    const isMobile = useIsMobile();
    const [fullInitialState, setFullInitialState] = useState<GridInitialStatePro | null>(null);

    const fullTableName = useMemo(() => {
        if (isNullOrWhiteSpace(tableSubName)) {
            return tableName;
        } else {
            return tableName + "_" + tableSubName;
        }
    }, [tableName, tableSubName]);

    const setFullInitialStateIfNotSet = useCallback((state: GridInitialStatePro) => {
        setFullInitialState(s => {return s ?? state});
    }, []);

    const getDataGridState = useCallback(() => {
        const currentStateString = localStorage.getItem(fullTableName);
        return currentStateString ? JSON.parse(currentStateString) : {};
    }, [fullTableName]);

    const apiRefToUse = useMemo(() => {
        if (apiRef) {
            return apiRef;
        } else {
            return internalApiRef;
        }
    }, [apiRef, internalApiRef]);

    const pageSizeToUse = useMemo(() => {
        if (pageSize !== undefined) {
            return pageSize;
        } else {
            return rowsPerPage.pageSize;
        }
    }, [pageSize, rowsPerPage]);

    const rowsPerPageOptionsToUse = useMemo(() => {
        if (rowsPerPageOptions !== undefined) {
            return rowsPerPageOptions;
        } else {
            return rowsPerPage.pageSizeOptions;
        }
    }, [rowsPerPageOptions, rowsPerPage]);

    const onPageSizeChangeInternal = useCallback((newPageSize: number, details: GridCallbackDetails) => {
        return rowsPerPage.setPageSize(newPageSize);
    }, [rowsPerPage]);

    const onPageSizeChangeToUse = useMemo(() => {
        if (onPageSizeChange !== undefined) {
            return onPageSizeChange;
        } else {
            return onPageSizeChangeInternal;
        }
    }, [onPageSizeChange, onPageSizeChangeInternal]);

    const columnManagerRestoreInternal = useCallback((initialState: GridInitialStatePro, storedState: GridInitialStatePro) => {
        let mergedState: GridInitialStatePro = {...initialState};
        if (!mergedState.columns) {
            mergedState.columns = {};
        }
        if (!mergedState.columns!.columnVisibilityModel) {
            mergedState.columns!.columnVisibilityModel = {};
        }
        if (!mergedState.columns!.dimensions) {
            mergedState.columns!.dimensions = {};
        }
        mergedState.columns!.orderedFields = [];
        if (!mergedState.pinnedColumns) {
            mergedState.pinnedColumns = {};
        }
        if (!mergedState.pinnedColumns!.left) {
            mergedState.pinnedColumns!.left = [];
        }
        if (!mergedState.pinnedColumns!.right) {
            mergedState.pinnedColumns!.right = [];
        }

        // For visibility, update with the stored ones
        if (storedState.columns?.columnVisibilityModel) {
            Object.entries(storedState.columns.columnVisibilityModel).forEach(([key, value]) => {
                mergedState.columns!.columnVisibilityModel![key] = value;
            });
        }

        // For width, just width from the stored ones
        if (storedState.columns?.dimensions) {
            Object.entries(storedState.columns.dimensions).forEach(([key, value]) => {
                if (mergedState.columns!.dimensions![key] !== undefined) {
                    mergedState.columns!.dimensions![key]["width"] = value.width;
                    mergedState.columns!.dimensions![key]["flex"] = value.flex;
                }
            });
        }

        // For order, default to stored order, then tack on anything that's new
        if (storedState.columns?.orderedFields) {
            mergedState.columns!.orderedFields = storedState.columns.orderedFields.slice();
        }

        if (initialState.columns?.orderedFields) {
            initialState.columns.orderedFields.forEach((value) => {
                if (mergedState.columns!.orderedFields!.findIndex((v) => v === value) === -1) {
                    mergedState.columns!.orderedFields!.push(value);
                }
            });
        }

        // For pins, just use the stored pins
        mergedState.pinnedColumns = storedState.pinnedColumns;

        return mergedState;
    }, []);

    const columnManagerRestoreToUse = useMemo(() => {
        if (columnManagerRestore) {
            return columnManagerRestore;
        } else {
            return columnManagerRestoreInternal;
        }
    }, [columnManagerRestore, columnManagerRestoreInternal]);

    const saveState = React.useCallback(() => {
        if ((rows?.length ?? 0) === 0 || !apiRefToUse.current) {
            // We don't send columns while we have no data. So don't clobber the stored state in that case
            return;
        }
        const currentState = apiRefToUse.current.exportState();

        // Somtimes, visible columns aren't in the visibility model, and their absence counts as "visible"
        // This causes logical oddities when default-hidden columns are set to be visible, so we need to be precise here
        apiRefToUse.current.getVisibleColumns().forEach(c => {
            if (!currentState.columns) {
                currentState.columns = {};
            }
            if (!currentState.columns.columnVisibilityModel) {
                currentState.columns.columnVisibilityModel = {};
            }
            currentState.columns.columnVisibilityModel[c.field] = true;
        });

        localStorage.setItem(fullTableName, JSON.stringify(currentState));
    }, [apiRefToUse, fullTableName, rows]);

    const modifyStateWithDefaults = useCallback((state: GridInitialStatePro) => {
        if (state.columns) {
            state.columns.columnVisibilityModel = {};
            hideByDefaultColumns?.forEach((c) => { state.columns!.columnVisibilityModel![c] = false });

            if (isMobile) {
                hideInMobileColumns?.forEach((c) => { state.columns!.columnVisibilityModel![c] = false });
            }
        }

        if (state.columns?.dimensions) {
            Object.keys(state.columns.dimensions).forEach((key) => {
                if (state.columns!.dimensions![key].minWidth === 130 && isMobile) {
                    state.columns!.dimensions![key].minWidth = 90;
                }
                if (state.columns!.dimensions![key].minWidth === 90 && !isMobile) {
                    state.columns!.dimensions![key].minWidth = 130;
                }
            });
        }
    }, [hideByDefaultColumns, hideInMobileColumns, isMobile]);

    const restoreState = useCallback(() => {
        // We don't send columns while we have no data. So don't try to restore the saved columns in that case
        if ((rows?.length ?? 0) !== 0 || !apiRefToUse.current) {
            let mergedState: GridInitialStatePro = {};
            const initialState = apiRefToUse.current.exportState();
            setFullInitialStateIfNotSet(structuredClone(initialState));
            modifyStateWithDefaults(initialState);

            try {
                const state = getDataGridState();
                if (state) {
                    // Build the merged state using stored state and initial state.
                    mergedState = columnManagerRestoreToUse(initialState, state);
                    apiRefToUse.current.restoreState(mergedState);
                } else {
                    apiRefToUse.current.restoreState(initialState);
                }
            } catch (e) {
                // Something went wrong trying to restore the state. Just default back to initial
                apiRefToUse.current.restoreState(initialState);
            }
        }
        setShowGrid(true);
    }, [apiRefToUse, getDataGridState, columnManagerRestoreToUse, setFullInitialStateIfNotSet, modifyStateWithDefaults, rows]);

    const resetState = useCallback(() => {
        if (fullInitialState) {
            modifyStateWithDefaults(fullInitialState);
            localStorage.setItem(fullTableName, JSON.stringify(fullInitialState));
            restoreState();
        }
    }, [restoreState, fullInitialState, fullTableName, modifyStateWithDefaults]);

    /* 
    * Column Manager Saving
    * We need to use a combination of useEffect and useLayoutEffect to correctly persist grid state when the data or columns change
    * The columns technically change when switching to mobile mode
    * Data can change if search parameters change, or in some setup screens when adding/deleting rows
    * Unfortunately, DataGrid resets its column state to the default if the columns change.
    * Using the OnStateChange listener isn't a good idea either, as it fires way to often and erratically to properly listen to 
    * useEffect fires after the grid has integrated its changes. 
    *   If we restore state in the useEffect, it won't get overridden by bothersome resets. useLayoutEffect fires too soon to restore
    * useLayoutEffect's return callback fires before the grid changes all its columns. It truly does use the state just before it changes
    *   The state we want to save is correct here, but not in useEffect.
    * Therefore, we save state in the return callback of useLayoutEffect, then restore it in useEffect, and listen for changes to rows and columns
    */
    useEffect(() => {
        restoreState();
    }, [restoreState, columns]);

    useLayoutEffect(() => {
        // handle refresh and navigating away/refreshing
        window.addEventListener('beforeunload', saveState);
        
        return () => {
            // in case of an SPA remove the event-listener
            window.removeEventListener('beforeunload', saveState);
            saveState();
        };
    }, [saveState, columns]);

    const gridTheme: SxProps<Theme> = useCallback((theme: Theme) => {
        return {
            '@media screen': {
                "& .MuiDataGrid-row": {
                    "&:nth-of-type(2n)": {
                        backgroundColor: alpha(theme.palette.secondary.light, 0.1),
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.secondary.light, 0.2),
                        },
                    },
                    "$ .Mui-selected": {
                        backgroundColor: alpha(theme.palette.secondary.dark, 0.5),
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.secondary.dark, 0.6),
                        },
                    }
                },
                "& .MuiDataGrid-iconButtonContainer, .MuiDataGrid-menuIcon": {
                    "& svg": {
                        fill: theme.palette.secondary.contrastText,
                    }
                },
                "& .MuiDataGrid-columnHeaders, .MuiDataGrid-pinnedColumnHeaders": {
                    background: theme.palette.secondary.main,
                    color: theme.palette.secondary.contrastText
                },
                ".MuiDataGrid-pointer": {
                    cursor: "pointer"
                },
            },
            '@media print': {
                '.MuiDataGrid-main': { color: 'rgba(0, 0, 0, 0.87)' }
            },
        }
    }, []);

    /*Set defaults for pagination behaviour.  They can be overridden by any props passed in */
    if (!rows) {
        return <>
            <Skeleton variant="text" height={40} />
            <Skeleton variant="rectangular" height={416} />
        </>
    } else if (rows.length > 0) {
        return <DataGridPro
            sx={gridTheme}
            pagination
            rowHeight={35}
            headerHeight={35}
            autoHeight={true}
            disableSelectionOnClick
            rows={rows}
            style={{ minHeight: 456, visibility: showGrid ? undefined : "hidden" }}
            localeText={{
                ...rest.localeText,
                ...localeText
            }}
            pageSize={pageSizeToUse}
            onPageSizeChange={onPageSizeChangeToUse}
            rowsPerPageOptions={rowsPerPageOptionsToUse}
            apiRef={apiRefToUse}
            initialState={{columns: {columnVisibilityModel: {}}}}
            columns={columns}
            components={{Toolbar: disableToolbar ? undefined : CustomDataGridToolbar}}
            componentsProps={{toolbar: {
                csvOptions: { fileName: tableName }, 
                printOptions: { hideFooter: true, hideToolbar: true, includeCheckboxes: false, fileName: tableName, disableToolbarButton: true }, 
                resetState: resetState, 
                disableRestoreGridStyle: disableRestoreGridStyle,
                disableGridExport: disableGridExport
            }}}
            {...rest}

        />
    } else {
        // No data, show no columns (keep in line with columns being a static declaration in a table)
        return <DataGridPro
            sx={gridTheme}
            rowHeight={35}
            headerHeight={35}
            autoHeight={true}
            rows={[]}
            style={{ minHeight: 456, visibility: showGrid ? undefined : "hidden" }}
            apiRef={apiRefToUse}
            localeText={{
                ...rest.localeText,
                ...localeText
            }}
            columns={[]}
        />
    }
};

export default CustomDataGridPro;